// localStorage-basierte Crash-/Reload-Recovery für die aktive
// Berechnung (Task #35). Greift für Free und Pro — Pro hat zwar
// Cloud-Save, der greift aber NUR wenn ein Projekt + eine Berechnung
// im Projekt-Store ausgewählt sind. Frisch geladene Vorlagen, .tcp-
// Importe und alle Free-Sessions hätten ohne Recovery keinen
// Sicherheitsnetz gegen versehentlichen Reload oder Browser-Crash.
//
// Snapshot ist ein flacher Klon der Berechnungs-Felder aus dem
// toleranceStore (elements, m0, mode, thermal, calculationName, mc-
// Parameter) PLUS die ownerId des eingeloggten Users.
//
// Warum die ownerId: Der Snapshot liegt unter EINEM globalen
// localStorage-Key, der Browser wird aber von mehreren Accounts geteilt
// (Vorbenutzer, neu angelegter Account, gemeinsamer Rechner). Ohne
// User-Bindung bot das Banner beim ersten Login eines neuen Accounts die
// Berechnung des Vorbenutzers an — der null→ID-Bootstrap in App.jsx
// löscht den Snapshot bewusst nicht. Mit ownerId wird ein fremder
// Snapshot beim Laden verworfen (und gleich gelöscht, damit keine
// fremden Berechnungsdaten im Browser zurückbleiben).

const KEY = 'toleranz:recovery:v1';

/**
 * Persistiert den Berechnungs-Snapshot für den übergebenen User. Wirft
 * nie — bei vollem Storage (QuotaExceeded), Privacy-Mode oder fehlendem
 * localStorage wird der Save still verworfen. Recovery ist eine
 * Bequemlichkeit, kein Korrektheits-Garant; ein "Speichern
 * fehlgeschlagen"-Toast hier wäre Lärm, weil der User regulär
 * weiterarbeiten kann.
 *
 * Ohne ownerId wird NICHT gespeichert: ein Snapshot ohne Besitzer ließe
 * sich beim Laden keinem Account zuordnen und würde nie wieder angeboten.
 *
 * @param {object} snapshot  Berechnungs-Felder aus dem toleranceStore
 * @param {string} ownerId   id des eingeloggten Users (auth.users.id)
 */
export function saveRecovery(snapshot, ownerId) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (!ownerId) return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify({
      savedAt: Date.now(),
      ownerId,
      ...snapshot,
    }));
  } catch (err) {
    console.warn('[recovery] save failed:', err.message);
  }
}

/**
 * Liest den Snapshot für den übergebenen User. Liefert null wenn keiner
 * existiert, der JSON-Blob nicht mehr parsbar ist (z.B. nach einem
 * inkompatiblen Schema-Bump), oder der Snapshot einem ANDEREN User
 * gehört. Ein fremder oder besitzerloser (Alt-)Snapshot wird dabei
 * gelöscht, damit keine fremden Berechnungsdaten im geteilten Browser
 * zurückbleiben und das Banner ihn nicht erneut anbietet.
 *
 * @param {string} currentUserId  id des aktuell eingeloggten Users
 */
export function loadRecovery(currentUserId) {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Minimal-Validierung: ohne elements ist der Snapshot wertlos.
    if (!parsed || !Array.isArray(parsed.elements)) return null;
    // User-Bindung: nur der eigene Snapshot wird angeboten. Ein fremder
    // (anderer Account am selben Browser) oder besitzerloser Alt-Snapshot
    // wird verworfen und gelöscht.
    if (!currentUserId || parsed.ownerId !== currentUserId) {
      clearRecovery();
      return null;
    }
    return parsed;
  } catch (err) {
    console.warn('[recovery] load failed:', err.message);
    return null;
  }
}

/**
 * Löscht den Snapshot. Idempotent. Wird aufgerufen, wenn der User:
 * - das Banner explizit verwirft,
 * - eine neue Berechnung startet ("Neu"),
 * - sich aus-/einloggt (User-Wechsel, Datenschutz),
 * - oder die Recovery wiederhergestellt hat (Snapshot ist nun aktiv).
 */
export function clearRecovery() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* localStorage gesperrt / Quota — Recovery ist best-effort */
  }
}
